import React, { useState, useEffect, useRef } from "react";
import { Html5QrcodeScanner } from "html5-qrcode";
import axios from "axios";
import { toast } from "react-hot-toast";
import { baseApiURL } from "../../baseUrl";

const QrScanner = () => {
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanHistory, setScanHistory] = useState([]);
  const [manualEntry, setManualEntry] = useState({
    enrollmentNo: "",
    date: new Date().toISOString().split("T")[0],
    status: "present",
    notes: ""
  });
  const scannerRef = useRef(null);

  useEffect(() => {
    // Initialize scanner
    const scanner = new Html5QrcodeScanner(
      "reader",
      {
        qrbox: {
          width: 250,
          height: 250,
        },
        fps: 5,
      },
      false
    );

    scannerRef.current = scanner;

    const onScanSuccess = async (decodedText) => {
      try {
        setLoading(true);
        const response = await axios.post(
          `${baseApiURL()}/transport/attendance/scan`,
          { qrData: decodedText },
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        if (response.data.success) {
          toast.success(response.data.message);
          setScanResult(response.data);
          
          // Add to scan history
          setScanHistory(prev => [response.data, ...prev.slice(0, 9)]);
          
          // Clear result after 3 seconds
          setTimeout(() => {
            setScanResult(null);
          }, 3000);
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        console.error("Scan error:", error);
        toast.error(error.response?.data?.message || "Scan failed");
      } finally {
        setLoading(false);
      }
    };

    const onScanFailure = (error) => {
      // Handle scan failure silently
      console.warn("QR scan error:", error);
    };

    scanner.render(onScanSuccess, onScanFailure);

    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear();
      }
    };
  }, []);

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualEntry.enrollmentNo) {
      toast.error("Please enter enrollment number");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${baseApiURL()}/transport/attendance/manual`,
        manualEntry,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        setScanResult(response.data);
        setManualEntry({
          enrollmentNo: "",
          date: new Date().toISOString().split("T")[0],
          status: "present",
          notes: ""
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Manual entry failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bento-header-banner flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">QR Code Scanner</h1>
          <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">Scan student QR codes to mark transport attendance</p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs p-6 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Scanner Section */}
          <div className="space-y-4">
            <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-4">
              <div id="reader" className="w-full"></div>
            </div>
            
            {loading && (
              <div className="flex items-center justify-center p-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            )}

            {scanResult && (
              <div className="bg-emerald-50/60 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">✅</div>
                  <div className="flex-1">
                    <h4 className="text-xs font-bold text-emerald-800 dark:text-emerald-300 uppercase tracking-wider">Attendance Recorded</h4>
                    <p className="text-xs text-emerald-700 dark:text-emerald-400 font-semibold mt-0.5">{scanResult.message}</p>
                    {scanResult.student && (
                      <div className="mt-2 space-y-1 text-xs text-slate-700 dark:text-slate-300">
                        <p><span className="font-bold">Name:</span> {scanResult.student.name}</p>
                        <p><span className="font-bold">Enrollment:</span> {scanResult.student.enrollmentNo}</p>
                        <p><span className="font-bold">Bus:</span> {scanResult.student.busNumber}</p>
                        <p><span className="font-bold">Seat:</span> {scanResult.student.seatNumber}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Manual Entry Section */}
          <div className="space-y-4">
            <div className="bg-slate-50/50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 space-y-4">
              <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Manual Attendance Entry</h3>
              
              <form onSubmit={handleManualSubmit} className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Enrollment Number
                  </label>
                  <input
                    type="text"
                    value={manualEntry.enrollmentNo}
                    onChange={(e) => setManualEntry(prev => ({
                      ...prev,
                      enrollmentNo: e.target.value.toUpperCase()
                    }))}
                    className="w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter enrollment number"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={manualEntry.date}
                    onChange={(e) => setManualEntry(prev => ({
                      ...prev,
                      date: e.target.value
                    }))}
                    className="w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Status
                  </label>
                  <select
                    value={manualEntry.status}
                    onChange={(e) => setManualEntry(prev => ({
                      ...prev,
                      status: e.target.value
                    }))}
                    className="w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="present">Present</option>
                    <option value="late">Late</option>
                    <option value="absent">Absent</option>
                    <option value="excused">Excused</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={manualEntry.notes}
                    onChange={(e) => setManualEntry(prev => ({
                      ...prev,
                      notes: e.target.value
                    }))}
                    className="w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                    rows="2"
                    placeholder="Any additional notes..."
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-2.5 rounded-xl font-bold text-xs shadow-xs transition cursor-pointer disabled:opacity-50"
                >
                  {loading ? "Processing..." : "Mark Attendance"}
                </button>
              </form>
            </div>

            {/* Recent Scans */}
            <div className="bg-slate-50/50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 space-y-3">
              <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Recent Scans</h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {scanHistory.length === 0 ? (
                  <p className="text-slate-400 text-xs text-center py-4">No scans yet</p>
                ) : (
                  scanHistory.map((scan, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs">
                      <div>
                        <p className="font-bold text-slate-900 dark:text-white">{scan.student?.name || scan.attendance?.enrollmentNo}</p>
                        <p className="text-[11px] text-slate-400">
                          {new Date(scan.attendance?.date || Date.now()).toLocaleTimeString()}
                        </p>
                      </div>
                      <span className={`px-2.5 py-0.5 rounded-lg text-[10px] font-bold border ${
                        scan.attendance?.status === "present" 
                          ? "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800" 
                          : scan.attendance?.status === "late"
                          ? "bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800"
                          : "bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800"
                      }`}>
                        {scan.attendance?.status || "present"}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-indigo-50/50 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-800/60 rounded-2xl p-6 space-y-3">
        <h3 className="text-xs font-bold text-indigo-900 dark:text-indigo-300 uppercase tracking-wider">How to Use</h3>
        <ul className="space-y-1.5 text-xs text-indigo-800 dark:text-indigo-300/80 font-medium">
          <li className="flex items-start gap-2">
            <span>1.</span>
            <span>Allow camera access when prompted</span>
          </li>
          <li className="flex items-start gap-2">
            <span>2.</span>
            <span>Position the student's QR code within the scanner frame</span>
          </li>
          <li className="flex items-start gap-2">
            <span>3.</span>
            <span>Attendance will be automatically recorded upon successful scan</span>
          </li>
          <li className="flex items-start gap-2">
            <span>4.</span>
            <span>Use manual entry for students without QR codes</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default QrScanner;